import { MessageBarType } from "@fluentui/react";
import { useState } from "react";
import { createUserRolesInfo, UserRolesModel } from "../../accounts/models/UserRolesInfo";
import { IDataResult, IResult, ResponseStatus } from "../../common/models/Result";
import { useForm } from "../../common/viewmodels/DefaultVM";
import { Authentication } from "../../common/views/Authentication";
import { DataUtils } from "../../common/views/DataUtils";
import { Utils } from "../../common/views/Utils";
import { emptyFunc, KeyValue, OptionModel } from "../../home/models/OtherTypes";
import { SortModel } from "../../home/models/PageTypes";
import { ShortWebsiteModel } from "../../websites/models/WebsiteModel";
import { WebsiteService } from "../../websites/services/WebsiteService";
import {
    createNewOrder,
    NewOrderModel, OrderFilterModel, OrderModel, OrderRejectReason,
    OrderRequestType, OrderStatus, OrderTracking
} from "../models/OrderModel";
import { OrderAdminService } from "../services/OrderAdminService";

export function useOrderAdminVM() {
    const orderService = new OrderAdminService();
    const websiteService = new WebsiteService();

    const [orders, setOrders] = useState<OrderModel[]>([]);
    const [counts, setCounts] = useState<KeyValue<OrderStatus, number>[]>([]);
    const [statuses, setStatuses] = useState<OptionModel[]>([]);
    const [selectedId, setSelectedId] = useState<number>(0);
    const [pageHash, setPageHash] = useState<number>(0);
    const [listHash, setListHash] = useState<number>(0);

    const [loading, setLoading] = useState(false);
    const [showAdd, setShowAdd] = useState(false);
    const [showDetail, setShowDetail] = useState(false);
    const [showFilter, setShowFilter] = useState(false);

    const [totalCount, setTotalCount] = useState<number>(0);
    const [userRoles, setUserRoles] = useState<UserRolesModel>(createUserRolesInfo());
    const [websites, setWebsites] = useState<ShortWebsiteModel[]>([]);

    const {
        form: filter,
        setForm: setFilter,
        resetForm: resetFilter,
        setField: setFilterField
    } = useForm<OrderFilterModel>({
        customerId: 0,
        isAllWarehouses: true,
        isOpen: true,
        isUnInvoiced: false,
        warehouseId: 0,
        status: OrderStatus.request,
        reason: OrderRejectReason.none,
        tracking: OrderTracking.none,
        requestType: OrderRequestType.all,
        search: "",
        pageNumber: 1,
        pageSize: 50,
        sortModel: new SortModel(),
        pagingDisabled: false
    });

    const {
        form: tempFilter,
        setForm: tempSetFilter,
        resetForm: tempResetFilter,
        setField: tempSetFilterField
    } = useForm<OrderFilterModel>({
        customerId: 0,
        isAllWarehouses: true,
        isOpen: true,
        isUnInvoiced: false,
        warehouseId: 0,
        status: OrderStatus.all,
        reason: OrderRejectReason.none,
        tracking: OrderTracking.none,
        requestType: OrderRequestType.all,
        search: "",
        pageNumber: 1,
        pageSize: 50,
        sortModel: new SortModel(),
        pagingDisabled: false
    });

    const onLoad = () => {
        console.log("Admin Order loaded.");
        setStatuses(getStatuses());
        getWebsites();
        getList();
        getCounts();
        setUserRoles(Authentication.getUserRoles());
    }

    const getList = (getCount = true, pageChange = false) => {
        setLoading(true);
        setOrders([]);
        setListHash(Utils.getRandomKey());

        if (!filter.status) filter.status = OrderStatus.all;
        if (!filter.reason) filter.reason = OrderRejectReason.none;

        const request = orderService.search(filter);

        DataUtils.onRequest(
            request,
            (result: IDataResult) => onSearchResult(result, pageChange)
        );

        if (getCount) getCounts();
    }

    const onSearchResult = (result: IDataResult, pageChange = false) => {
        if (result.status === ResponseStatus.success && result.data) {
            const orders = result.data.items as OrderModel[];
            const totalCount = result.data.totalCount;

            setOrders(orders);
            setTotalCount(totalCount);
            setListHash(Utils.getRandomKey());

            if (!pageChange) setPageHash(Utils.getRandomKey() + 100);
        }

        setLoading(false);
    }

    const getCounts = () => {
        setCounts([]);
        const request = orderService.getCountByStatus(filter);
        DataUtils.onRequest(request, onGetCountsResult);
    }

    const onGetCountsResult = (result: IDataResult) => {
        var counts = result.data as KeyValue<string, number>[];

        if (counts) {
            const statuses = getStatuses();

            for (let i = 0; i < statuses.length; i++) {
                const option = statuses[i];
                const status = option.key;
                const count = counts.find(
                    (item: KeyValue<string, number>) => item.key === status
                );

                option.count = count ? count.value : 0;
            }

            setStatuses(statuses);
        }
    }

    const placeOrder = (id: number, onCompleted: emptyFunc) => {
        const onResult = (result: IDataResult) => {
            if (result.status === ResponseStatus.success) {
                onCompleted();
            }
        }

        if (window.confirm("Would you like to place order?")) {
            const request = orderService.placeOrder(id);
            DataUtils.onRequest(request, onResult);
        }
    }

    const getStatuses = (): OptionModel[] => {
        const option1 = new OptionModel();
        option1.key = OrderStatus.request;
        option1.text = "Request";
        option1.current = true;
        option1.count = 0;

        const option2 = new OptionModel();
        option2.key = OrderStatus.quote;
        option2.text = "Quote";
        option2.current = false;
        option2.count = 0;

        const option3 = new OptionModel();
        option3.key = OrderStatus.orderPending;
        option3.text = "Pending";
        option3.current = false;
        option3.count = 0;

        const option4 = new OptionModel();
        option4.key = OrderStatus.orderApproved;
        option4.text = "Approved";
        option4.current = false;
        option4.count = 0;

        const option5 = new OptionModel();
        option5.key = OrderStatus.closed;
        option5.text = "Closed";
        option5.current = false;
        option5.count = 0;

        return [option1, option2, option3, option4, option5];
    }

    const getWebsites = () => {
        const onResult = (result: IDataResult) => {
            const list = result.data as ShortWebsiteModel[];
            setWebsites(list);
        };

        var request = websiteService.getOpenList();
        DataUtils.onRequest(request, onResult);
    }

    return {
        orders,
        counts,

        filter,
        setFilter,
        resetFilter,
        setFilterField,

        tempFilter,
        tempSetFilter,
        tempResetFilter,
        tempSetFilterField,

        loading,
        setLoading,

        onLoad,
        getList,
        getCounts,
        placeOrder,

        selectedId,
        setSelectedId,
        showAdd,
        setShowAdd,
        showDetail,
        setShowDetail,
        showFilter,
        setShowFilter,
        totalCount,
        statuses,
        setStatuses,
        websites,
        userRoles,
        pageHash,
        listHash
    }
}